/**
 * HTTP client for the HOGC One Payment module — a generic transaction ledger.
 *
 * A caller opens a transaction, receives a `reference` and a HitPay
 * `checkoutUrl`, then polls the ledger by reference until it reports PAID.
 * Confirmation is webhook-driven server-side, so the ledger is the single source
 * of truth for status — never the browser outcome or HitPay's redirect.
 *
 *   POST /course-access/:programSlug/purchase → { reference, displayReference, status, checkoutUrl }
 *   GET  /payments/transactions/:ref          → { reference, status, amountCents, ... }
 */
import axios from 'axios';
import {
  isPaymentDemoActive,
  demoCreateTransaction,
  demoGetTransaction,
  demoReconcileTransaction,
} from './payment-demo';

// Payments live under the versioned path on the API: <VUE_APP_API_URL>/v2/payments
// (backend controller is @Controller('api/v2/payments'); VUE_APP_API_URL already ends in /api).
const paymentsBaseUrl = () => `${process.env.VUE_APP_API_URL}/v2/payments`;

/**
 * Mint an idempotency key for a transaction create.
 *
 * One per *purchase attempt*, not per request: replaying the same key must
 * return the same reference rather than opening a second transaction, which is
 * what stops a double-tap or a flaky retry from charging twice. A new attempt
 * (the member tapping "Try again" after a failure) gets a new key, because the
 * failed row's checkout is spent and cannot be replayed.
 */
export function newIdempotencyKey() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `hogc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  );
}

/**
 * Create a payment transaction on the ledger.
 * `memberId` is derived server-side from the auth token, never sent by the client.
 *
 * @param {Object} params
 * @param {string} params.token         Bearer access token.
 * @param {string} params.productType   Opaque consumer tag, e.g. 'offering'.
 * @param {string} params.productId     Domain id, e.g. a reference code / slug.
 * @param {number} params.amountCents    Amount in minor units (server-authoritative for fixed-price products).
 * @param {string} params.currency      ISO-4217, e.g. 'SGD'.
 * @param {string} params.idempotencyKey Retried creates return the same reference.
 * @returns {Promise<{reference: string, displayReference: string|null, status: string, checkoutUrl: string|null}>}
 */
export async function createPaymentTransaction({
  token,
  productType,
  productId,
  productName,
  amountCents,
  currency,
  idempotencyKey,
  metadata,
}) {
  if (isPaymentDemoActive()) return demoCreateTransaction();
  const { data } = await axios.post(
    `${paymentsBaseUrl()}/transactions`,
    { productType, productId, productName, amountCents, currency, idempotencyKey, metadata },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return data;
}

/**
 * List the authenticated member's transactions (newest first) for the
 * "My Payments" history screen. `memberId` is derived server-side from the token.
 *
 * @param {Object} params
 * @param {string} params.token   Bearer access token.
 * @param {number} [params.limit] Max rows (default 50, capped server-side at 100).
 * @returns {Promise<Array<{reference: string, status: string, amountCents: number, currency: string, productType: string, productId: string, provider: string, metadata: object|null, paidAt: string|null, createdAt: string}>>}
 */
export async function listPaymentTransactions({ token, limit }) {
  const { data } = await axios.get(`${paymentsBaseUrl()}/transactions`, {
    params: limit ? { limit } : undefined,
    headers: { Authorization: `Bearer ${token}` },
  });
  return data?.transactions || [];
}

/**
 * Fetch a transaction's current status by reference (poll target).
 *
 * @param {Object} params
 * @param {string} params.token     Bearer access token.
 * @param {string} params.reference Ledger reference returned by create.
 * @returns {Promise<{reference: string, status: string, amountCents: number, currency: string, productType: string, productId: string}>}
 */
export async function getPaymentTransaction({ token, reference }) {
  if (isPaymentDemoActive()) return demoGetTransaction(reference);
  const { data } = await axios.get(`${paymentsBaseUrl()}/transactions/${reference}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

/**
 * Ask the server to reconcile a transaction against HitPay *now* — used when the
 * webhook is late (the in-session poll has been PENDING a while). The server
 * queries HitPay only if the row is still open and settles it, then returns the
 * (possibly updated) status. Safe to call repeatedly; a no-op on terminal rows.
 *
 * @param {Object} params
 * @param {string} params.token     Bearer access token.
 * @param {string} params.reference Ledger reference.
 * @returns {Promise<{reference: string, status: string, amountCents: number, currency: string, productType: string, productId: string}>}
 */
export async function reconcilePaymentTransaction({ token, reference }) {
  if (isPaymentDemoActive()) return demoReconcileTransaction(reference);
  const { data } = await axios.post(
    `${paymentsBaseUrl()}/transactions/${reference}/reconcile`,
    {},
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return data;
}
